/**
 * redactSecretPath — shared client-side secret-path / secret-payload scrubber.
 *
 * SINGLE SOURCE OF TRUTH for stripping single-use tokens and contact PII out of
 * anything that leaves the browser to a telemetry sink (Sentry error events,
 * Sentry breadcrumbs, Sentry Replay recordings, GA4/analytics page views,
 * React-Query mutation-variable / query-key captures).
 *
 * Why this exists (round-8d finding #3, §4.3 client PII/token exfil):
 *   The marketplace ships several token-bearing deep links whose path segment
 *   IS the credential —
 *     - /connect/respond/:token           (single-use Connect owner-action token)
 *     - /owner/claims/verify/:token        (single-use claim-verify token)
 *     - /invite/:token                     (account-less invite funnel)
 *     - /unsubscribe/:token                (outreach unsubscribe token)
 *     - /suppressions/:email               (outreach suppression — raw email PII)
 *   Left un-scrubbed, `location.pathname` (and any URL derived from it) flows
 *   verbatim into Sentry `event.request.url` / `transaction` / breadcrumbs /
 *   replay URLs and into the GA4 `page_path` / `page_location`, persisting a live
 *   credential (or a raw email) in a third-party store. React-Query error capture
 *   is a second exfil path: mutation `variables` carry the investor message + work
 *   email (requestConnect / updateProfile) and a query-key can embed a token
 *   (`['owner','invite', <token>]`).
 *
 * This mirrors the backend `redactPathTokens` helpers (my-backend/src/api/{connect,
 * owner}.ts) but is CLIENT-scoped: the backend scrubs a single router's path, the
 * client scrubs a full pathname that could carry ANY of the token-bearing prefixes,
 * so the prefix set here is the union of every marketplace token/PII path.
 */

/**
 * Token/PII-bearing path prefixes. For each, the ONE path segment immediately
 * following the prefix is the credential (or raw email) and must be redacted.
 * The regex captures the prefix (group 1) and replaces the following segment,
 * preserving any trailing segment (e.g. a `/peek` suffix) for diagnostic value.
 *
 * `g` + `i`: redact every occurrence, case-insensitively (a URL may repeat a
 * prefix, and hosted links are not case-normalised).
 */
const SECRET_PATH_SEGMENT_PATTERNS: readonly RegExp[] = [
  /(\/respond\/)[^/?#]+/gi,
  /(\/verify\/)[^/?#]+/gi,
  /(\/invite\/)[^/?#]+/gi,
  /(\/unsubscribe\/)[^/?#]+/gi,
  /(\/suppressions\/)[^/?#]+/gi,
];

/**
 * Round-12 F6 (§4.3 PII, backend↔FE parity): redact the OBJECT LEAF of a GCS owner-file
 * storage path embedded in a URL string. An owner file's object path is
 * `.../active-raises/{projectId}/{visibility}/{uuid}_{filename}`, and the `{filename}` leaf
 * is the investor-facing original filename (PII — it can embed an LP/investor name, e.g.
 * `Jane_Doe_passport.pdf`). A signed upload/download URL
 * (`https://storage.googleapis.com/<bucket>/active-raises/.../{uuid}_{filename}?…`) carries
 * that leaf in the PATH, so it flows verbatim into a Sentry span `description` /
 * `data['http.url']` where only this URL-string scrubber runs (the payload-KEY rules apply
 * to object keys, not to a raw URL string).
 *
 * This is a byte-for-byte port of the backend `GCS_OWNER_OBJECT_LEAF_RE`
 * (my-backend/src/utils/sanitizeObservability.ts) — the FE previously had NO equivalent,
 * leaving a parity gap after round-12 F2 added only `X-Goog-*`/`signature` QUERY-KEY
 * scrubbing (not the path leaf). It keeps the opaque prefix + `{uuid}_` and replaces the
 * filename with the placeholder → `{uuid}_[redacted]`.
 *
 * The leaf pattern deliberately spans to a `?`/`#`/`/` boundary (`[^?#/]`) so a filename is
 * fully redacted up to the query/fragment/next segment (a filename with spaces would be
 * percent-encoded in a URL, so it never contains a raw `/`). The regex is `/g`i and
 * module-level (stateful `lastIndex`) — {@link redactGcsObjectLeaf} resets it before each use.
 */
const GCS_OWNER_OBJECT_LEAF_RE = /(active-raises\/[^?#]*?\/[^?#/_]+_)[^?#/]+/gi;

const REDACTED = '[redacted]';

/**
 * Redact the owner-object filename leaf from a GCS storage path embedded in a URL string.
 * Fast-paths any string that does not contain the owner-object root. Resets the shared `/g`
 * regex's `lastIndex` before `.replace` (it is stateful). Returns the input unchanged when
 * no owner-object leaf is present.
 */
function redactGcsObjectLeaf(url: string): string {
  if (url.length > MAX_PII_SCAN_LENGTH) return url;
  if (url.indexOf('active-raises/') < 0) return url;
  GCS_OWNER_OBJECT_LEAF_RE.lastIndex = 0;
  return url.replace(GCS_OWNER_OBJECT_LEAF_RE, `$1${REDACTED}`);
}

/**
 * Redact single-use-token / raw-email segments from a URL PATHNAME (or the path
 * portion of a full URL string — the query/hash after the segment are left
 * untouched by the `[^/?#]` boundary). This is the PATH-only scrubber; for a
 * complete URL (path + query + hash + referrer/href) use `redactSecretUrl`,
 * which also strips sensitive query-parameter VALUES and the hash fragment.
 *
 * Idempotent: re-running over an already-redacted path is a no-op. Non-string /
 * empty input is returned unchanged so callers never have to guard.
 */
export function redactSecretPath(pathname: string): string {
  if (typeof pathname !== 'string' || pathname.length === 0) return pathname;
  let out = pathname;
  for (const pattern of SECRET_PATH_SEGMENT_PATTERNS) {
    // Reset lastIndex defensively — these are module-level `g` regexes and
    // `.replace` does not rely on lastIndex, but resetting avoids any surprise
    // if one is ever reused with `.test()`/`.exec()`.
    pattern.lastIndex = 0;
    out = out.replace(pattern, `$1${REDACTED}`);
  }
  // Round-12 F6: drop the owner-object filename leaf from a GCS storage path embedded in
  // the URL string (a signed upload/download URL carries `.../{uuid}_{filename}`). The leaf
  // is investor-facing PII; this keeps the `{uuid}_` prefix and redacts the filename.
  out = redactGcsObjectLeaf(out);
  return out;
}

/**
 * Sensitive query-parameter names. When a URL query string carries one of these
 * keys, its VALUE is a credential or PII and must be redacted before the URL
 * reaches any telemetry sink. Matched case-insensitively as a substring so
 * `invite`, `inviteToken`, `access_token`, `user_email`, etc. are all caught.
 *
 * `invite` (round-9 #4) is the load-bearing addition: the claim funnel ships
 * `/raises/:id/claim?invite=<single-use-token>`, and without this the raw invite
 * token flows into GA `search` / `page_location` and the Sentry transaction URL.
 */
const SECRET_QUERY_KEY_PATTERNS: readonly string[] = [
  'invite',
  'token',
  'email',
  'mail',
  'secret',
  'password',
  'pwd',
  'pass',
  'credential',
  'apikey',
  'api_key',
  'authorization',
  'auth',
  'jwt',
  'code',
  'otp',
  'pin',
  'ssn',
  'phone',
  'tel',
  'session',
  'key',
  // round-12 F2: FE↔backend parity — a signed GCS URL's X-Goog-Signature grants
  // time-bounded object access; its VALUE is a secret (backend sanitizeObservability
  // already treats `signature` + any `x-goog-*` param as sensitive). Without these the FE
  // shipped `X-Goog-Signature=<secret>` raw to Sentry/GA.
  'signature',
];

function isSecretQueryKey(key: string): boolean {
  const k = key.toLowerCase();
  // Any GCS `x-goog-*` signing param (credential/signature/date/expires/…) is sensitive,
  // mirroring the backend's `startsWith('x-goog-')` rule.
  if (k.startsWith('x-goog-')) return true;
  return SECRET_QUERY_KEY_PATTERNS.some((pattern) => k.includes(pattern));
}

/**
 * Redact sensitive VALUES out of a raw query string (with or without the leading
 * `?`). Preserves parameter order and benign values; replaces the value of any
 * key matching `SECRET_QUERY_KEY_PATTERNS` with `[redacted]`. Returns the string
 * WITHOUT a leading `?` (the caller re-attaches the separator it split on) so it
 * composes cleanly for both bare-path and full-URL inputs.
 *
 * If the query string cannot be parsed, the whole thing is dropped (`''`) rather
 * than risk shipping a raw credential — fail closed.
 */
function redactQueryString(rawQuery: string): string {
  const q = rawQuery.startsWith('?') ? rawQuery.slice(1) : rawQuery;
  if (q.length === 0) return '';
  try {
    const params = new URLSearchParams(q);
    const out = new URLSearchParams();
    let touched = false;
    for (const [key, value] of params) {
      if (isSecretQueryKey(key)) {
        out.append(key, REDACTED);
        touched = true;
      } else if (
        containsEmailValue(value) ||
        isLinkedInUrlValue(value) ||
        containsEncodedPiiValue(value)
      ) {
        // Round-13 #10 (§4.3 PII): a benign KEY can carry a PII VALUE — most
        // concretely `?utm_content=jane%40fund.com`, which `URLSearchParams`
        // decodes (`%40`→`@`) so the raw email reaches the telemetry sink. The
        // key-only `isSecretQueryKey` guard misses it, so we also inspect the
        // DECODED value: a bare email or LinkedIn profile URL is redacted whole,
        // regardless of the (non-secret) key it rides under. Fail-safe over-redact.
        //
        // Round-14 #9: `URLSearchParams` decodes only ONE encoding layer, so a
        // double-encoded PII value (`?utm_content=jane%2540fund.com` →
        // `jane%40fund.com`) still hides a literal-`@`-free string the singly-decoded
        // checks miss. `containsEncodedPiiValue` bounded-iteratively decodes the value
        // and re-tests it (fail-closed on a malformed / over-encoded escape) to close
        // that residual channel.
        out.append(key, REDACTED);
        touched = true;
      } else {
        out.append(key, value);
      }
    }
    // `URLSearchParams.toString()` percent-encodes; that is fine for a telemetry
    // value (it never round-trips back into navigation). `touched` is only used
    // to keep the fast path allocation-light in the common no-secret case.
    return touched || params.toString() !== q ? out.toString() : q;
  } catch {
    // Unparseable query → drop it entirely rather than leak a raw credential.
    return REDACTED;
  }
}

/**
 * Scrub a COMPLETE URL (or any path/URL-ish string) of every credential / PII
 * channel a telemetry sink can persist:
 *   - direct email / LinkedIn / encoded PII   → whole value replaced with `[redacted]`
 *   - path segments (token/email prefixes)  → via `redactSecretPath`
 *   - sensitive query-parameter VALUES        → via `redactQueryString`
 *   - the hash/fragment                        → dropped to `#[redacted]` when
 *     non-empty (fragments can carry tokens on SPA deep links and are never
 *     needed for telemetry diagnostics)
 *
 * Accepts both absolute URLs (`https://host/path?q#h`) and bare paths
 * (`/path?q#h`), which is exactly the mix Sentry (`request.url`, `transaction`)
 * and GA (`page_path`, `page_location`, `referrer`) hand us. Idempotent and
 * guard-free on non-string / empty input.
 */
export function redactSecretUrl(url: string): string {
  if (typeof url !== 'string' || url.length === 0) return url;
  if (isLinkedInUrlValue(url) || containsEmailValue(url) || containsEncodedPiiValue(url)) {
    return REDACTED;
  }

  // Split off the hash first (everything after the FIRST `#`), then the query
  // (everything after the FIRST `?` in the remainder). Manual splitting — not
  // `new URL()` — because we must handle bare paths and must never throw.
  const hashIdx = url.indexOf('#');
  const hash = hashIdx >= 0 ? url.slice(hashIdx) : '';
  const beforeHash = hashIdx >= 0 ? url.slice(0, hashIdx) : url;

  const queryIdx = beforeHash.indexOf('?');
  const rawQuery = queryIdx >= 0 ? beforeHash.slice(queryIdx + 1) : '';
  const pathPart = queryIdx >= 0 ? beforeHash.slice(0, queryIdx) : beforeHash;

  const scrubbedPath = redactSecretPath(pathPart);

  let out = scrubbedPath;
  if (rawQuery.length > 0) {
    const scrubbedQuery = redactQueryString(rawQuery);
    out += scrubbedQuery.length > 0 ? `?${scrubbedQuery}` : '';
  }
  // A non-empty fragment is dropped wholesale — SPA deep-link fragments can carry
  // single-use tokens and are never load-bearing for telemetry. A bare `#` is
  // preserved as-is (no secret to hide).
  if (hash.length > 1) {
    out += `#${REDACTED}`;
  } else if (hash.length === 1) {
    out += hash;
  }
  return out;
}

/**
 * True when a path contains a token/PII-bearing segment that redactSecretPath
 * would scrub. Used by tests and by callers that want to branch on presence.
 */
export function hasSecretPathSegment(pathname: string): boolean {
  if (typeof pathname !== 'string' || pathname.length === 0) return false;
  return redactSecretPath(pathname) !== pathname;
}

/**
 * Object keys whose VALUES are PII or credentials and must never be serialised
 * raw to a telemetry sink. Matched case-insensitively as a substring so
 * `investorEmail`, `workEmail`, `access_token`, `csrfToken`, etc. are all caught.
 */
const SECRET_PAYLOAD_KEY_PATTERNS: readonly string[] = [
  'email',
  'mail',
  'token',
  'secret',
  'password',
  'pwd',
  'pass',
  'credential',
  'apikey',
  'api_key',
  'authorization',
  'jwt',
  'ssn',
  'phone',
  'tel',
  // Round-10 #16: a failed entitlement request (submitEntitlementRequest) carries the
  // requester's `linkedinUrl` — a raw, directly-identifying contact URL — and, in error
  // capture, that whole variables object rides into React Query's Sentry mutation context.
  // `linkedinUrl` matched NONE of the prior key patterns, so it shipped verbatim. Add
  // `linkedin` (catches `linkedinUrl`, `linkedin_url`, `linkedinProfile`) and `contact`
  // (catches `contactEmail`, `contactPhone`, `contactUrl`) as first-class secret keys.
  'linkedin',
  'contact',
  // Free-text investor-authored fields that can contain arbitrary PII.
  'note',
  'message',
  'firstname',
  'lastname',
  'first_name',
  'last_name',
  'fullname',
  'full_name',
  // Round-9 #5c: generic keys that carry a user-supplied filename / free text on
  // marketplace mutation variables. `usePatchOwnerFile` / `useDeleteOwnerFile`
  // (useOwnerFiles.ts) carry the raw document filename under `name`; a rename
  // form carries the new label under `value`; direct filename fields under
  // `filename`. A leaked filename is §4.3 PII (it can embed an investor/LP name,
  // e.g. `Jane_Doe_passport.pdf`). Substring-matching `name`/`value` OVER-redacts
  // some benign diagnostic keys (`displayName` is already sensitive; `count`-like
  // values are unaffected) — that is the SAFE direction for a telemetry scrubber:
  // it only ever reduces diagnostic fidelity, never leaks.
  'name',
  'value',
  'filename',
];

function isSecretKey(key: string): boolean {
  const k = key.toLowerCase();
  return SECRET_PAYLOAD_KEY_PATTERNS.some((pattern) => k.includes(pattern));
}

/**
 * VALUE-aware LinkedIn-profile-URL detector (round-10 #16). A LinkedIn profile URL is a
 * directly-identifying contact handle, and it can ride into a telemetry sink under a key
 * that is NOT obviously secret (a generic `url`, `returnTo`, a free-text `note`/`message`,
 * or a bare positional array element). `isSecretKey` only guards KEYS, so a value-level
 * check closes the residual: any string that IS a LinkedIn profile URL is redacted whole,
 * regardless of the key it sits under. Matches the canonical `linkedin.com/in/<handle>`
 * (and `/pub/`, `/company/`, and regional `xx.linkedin.com`) shapes, case-insensitively.
 *
 * Deliberately NARROW (round-10 #24, over-redaction guard): it fires ONLY on an actual
 * LinkedIn URL — never on the substring "linkedin" in ordinary prose — so legitimate
 * observability (e.g. an error message that merely mentions LinkedIn) is not destroyed.
 */
const LINKEDIN_URL_PATTERN =
  /https?:\/\/([a-z0-9-]+\.)?linkedin\.com\/(in|pub|company)\/[^\s"'<>]+/i;

/**
 * Round-15 N1 (§4.3 ReDoS / unbounded-CPU, fail-closed): {@link EMAIL_VALUE_PATTERN} and
 * {@link LINKEDIN_URL_PATTERN} run on the MAIN THREAD inside Sentry's `beforeSend` over
 * attacker-influenceable input (e.g. a `?utm_content=<big>` query value that flows into a
 * telemetry URL). `EMAIL_VALUE_PATTERN` — `/[A-Z0-9._%+-]+@.../` — is O(n²) on a long
 * `%`-heavy string with no `@`: the `[A-Z0-9._%+-]+` prefix matches the whole run at every
 * start offset before failing the required `@`, so a multi-KB pathological value can pin a
 * CPU core in the error handler. A real email / LinkedIn URL is small; any value past this
 * cap is treated as PII (fail closed → redact) rather than fed to the quadratic regex. The
 * cap is generous (well above any legitimate email or profile URL) so it never over-redacts
 * a benign short value — it fires ONLY on adversarial length.
 */
const MAX_PII_SCAN_LENGTH = 2048;

/**
 * True when a value is too long to safely scan with the quadratic PII regexes (round-15 N1).
 * The caller fails CLOSED on `true` (treats the value as PII / redacts) so no untrusted value
 * of pathological length ever reaches {@link EMAIL_VALUE_PATTERN} / {@link LINKEDIN_URL_PATTERN}.
 */
function exceedsPiiScanLength(value: string): boolean {
  return value.length > MAX_PII_SCAN_LENGTH;
}

function isLinkedInUrlValue(value: string): boolean {
  // Round-15 N1: fail closed on an over-length value rather than run the regex on it.
  if (exceedsPiiScanLength(value)) return true;
  return LINKEDIN_URL_PATTERN.test(value);
}

/**
 * VALUE-aware email detector (round-10 #10-FE). A raw email is directly-identifying PII and can
 * ride into a telemetry sink under a NON-secret key — most concretely as a `utm_content` value
 * (`?utm_content=jane@fund.com`), which `isSecretKey`/`redactSecretUrl` both miss (the key is
 * benign, the value is not a URL). This detector redacts any string that CONTAINS an email so a
 * bare-email value can never persist. Deliberately conservative (an `@`-bearing local+domain
 * shape) to stay NARROW (round-10 #24): it fires on an actual email, not on prose containing an
 * `@` handle without a domain.
 */
const EMAIL_VALUE_PATTERN = /[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}/i;

function containsEmailValue(value: string): boolean {
  // Round-15 N1 (§4.3 ReDoS): `EMAIL_VALUE_PATTERN` is O(n²) on a long `%`-heavy no-`@`
  // string and runs on the main thread over attacker input in Sentry `beforeSend`. Fail
  // CLOSED on an over-length value (treat as PII) rather than run the quadratic regex on it.
  if (exceedsPiiScanLength(value)) return true;
  return EMAIL_VALUE_PATTERN.test(value);
}

/**
 * Round-14 #9 (§4.3 PII, fail-closed): the value-aware email/LinkedIn detectors only fire on
 * a SINGLY-decoded string. A telemetry URL / payload can carry the PII behind an EXTRA layer
 * of percent-encoding — most concretely `?utm_content=jane%2540fund.com`, where
 * `URLSearchParams` decodes once (`%25`→`%`) to `jane%40fund.com`, a string with no literal
 * `@` that {@link EMAIL_VALUE_PATTERN} misses. Double-encoded LinkedIn URLs and encoded
 * bare-email payload strings have the identical gap. `decodeURIComponent` only peels ONE
 * layer, so we peel iteratively until the string stops changing (fully decoded) or the cap is
 * hit — then the caller re-tests the fully-decoded copy through the PII detectors.
 *
 * The iteration cap is a DoS/pathological-input guard: a benign value decodes in 1–2 passes;
 * anything still shrinking after {@link MAX_DECODE_ITERATIONS} passes is adversarial and is
 * treated as PII (fail-closed) by the caller, since we can no longer prove it is benign.
 *
 * PER-ESCAPE decoding (CX#12 fix, over-redaction): each pass decodes only well-formed,
 * CONTIGUOUS `%HH` run(s) — never the whole string in one `decodeURIComponent` call. A single
 * `decodeURIComponent(value)` throws on ANY malformed escape ANYWHERE in the string, which
 * previously meant one unrelated bare `%` (e.g. `save 50% during spring%20campaign` — a
 * literal percent-space, not an escape) poisoned the whole-string decode, and the catch
 * handler then found the UNRELATED valid `%20` elsewhere and flagged the entire benign string
 * malformed. Decoding per contiguous-escape-run instead means:
 *   - a well-formed run (`%20`, or a multi-byte UTF-8 sequence like `%E2%82%AC`) decodes
 *     normally, in place;
 *   - a run that fails to decode (a genuinely invalid escape, e.g. `%zz`) is left as its
 *     original literal text — it does NOT abort decoding of the rest of the string, and it
 *     does NOT by itself flip `malformed`;
 *   - a bare `%` that isn't followed by 2 hex digits (e.g. `50% `) is never matched by the
 *     `%HH` regex at all, so it is trivially preserved as a literal character.
 * A genuinely encoded secret is unaffected — every well-formed escape (single- or
 * multi-layer) still fully decodes across passes and the PII re-test still fires on it.
 *
 * Returns `{ decoded, malformed }`:
 *   - `decoded`   — the fully (bounded-)decoded copy, for the PII re-test.
 *   - `malformed` — RESERVED for decode-depth-cap-exhaustion ONLY: the cap was exhausted while
 *                   an escape (`%`) still remains in the string, i.e. it would need a further
 *                   pass we refuse to run, so we can no longer prove it benign. An isolated
 *                   invalid/literal `%` that never resolves to an escape is NOT malformed on
 *                   its own — see `save 50% during spring%20campaign` above.
 *
 * This never mutates observability of a genuinely benign value: the caller emits the ORIGINAL
 * (un-decoded) value on a no-match, so decoding is used ONLY to widen detection, never to
 * rewrite benign output.
 */
const MAX_DECODE_ITERATIONS = 4;

/** Matches one or more BACK-TO-BACK well-formed `%HH` escapes — a contiguous run is decoded
 * together (not byte-by-byte) so a multi-byte UTF-8 sequence like `%E2%82%AC` (€) round-trips
 * correctly; decoding each `%HH` in isolation would feed a lone continuation byte to
 * `decodeURIComponent` and throw. */
const PERCENT_ESCAPE_RUN_RE = /(?:%[0-9A-Fa-f]{2})+/g;

/**
 * Round-19 #8 (§4.3 PII, fail-closed): a matched `%HH` run can MIX an invalid-UTF-8 byte with
 * otherwise-valid encoded PII — e.g. `%FF%6A%61%6E%65%25%34%30%66%75%6E%64%2E%63%6F%6D`, where
 * `%FF` alone is not a valid UTF-8 lead byte, so `decodeURIComponent` of the WHOLE run throws,
 * even though the remainder of the same run is `jane%40fund.com` waiting to be found. Simply
 * leaving a failed run as literal text (as CX#12's fixed point does) is correct for a run that
 * is ENTIRELY unrelated to any escape (that case is handled by the run regex never matching a
 * bare `%` in the first place — see `save 50% during spring%20campaign`), but is UNSOUND for a
 * run that DID match `%HH` syntax and still failed: that shape is exactly how PII hides.
 *
 * This performs a bounded best-effort split: shrink the run from the right until a maximal
 * PREFIX decodes, emit it, then repeat on the remainder — so `%FF` (undecodable alone) is
 * isolated as a single literal token while `%6A%61%6E%65%25%34%30%66%75%6E%64%2E%63%6F%6D`
 * still decodes and is surfaced for the PII re-test on a later pass. Any token that fails even
 * in isolation is left as literal `%HH` text and flips `anyUndecodable`, which the caller uses
 * to fail closed (§1 root cause: prove benign or redact — never assume benign on an undecodable
 * escape). Bounded by {@link MAX_SPLIT_TOKENS}: a run with more `%HH` tokens than that never
 * runs the O(k²) shrink-search — it is left as a single literal (with `anyUndecodable: true`),
 * matching the file's existing length-based DoS-guard posture (`MAX_PII_SCAN_LENGTH`).
 */
const MAX_SPLIT_TOKENS = 64;

/**
 * Attempt to decode a run that failed whole-run `decodeURIComponent`, token-by-token with
 * right-shrinking backoff (see {@link MAX_SPLIT_TOKENS} doc above). Returns the best-effort
 * decoded text and whether any token/sub-span could not be decoded even in isolation.
 */
function splitDecodeRun(run: string): { text: string; anyUndecodable: boolean } {
  const tokenCount = run.length / 3; // each `%HH` token is exactly 3 chars
  if (tokenCount > MAX_SPLIT_TOKENS) {
    // Bounded-cost guard: refuse the O(k²) shrink-search on a pathologically long run. Leave it
    // literal and fail closed — we cannot prove it benign without the search we're declining.
    return { text: run, anyUndecodable: true };
  }
  const tokens: string[] = [];
  for (let i = 0; i < run.length; i += 3) tokens.push(run.slice(i, i + 3));

  const parts: string[] = [];
  let anyUndecodable = false;
  let i = 0;
  while (i < tokens.length) {
    let matched = false;
    for (let j = tokens.length; j > i; j--) {
      const candidate = tokens.slice(i, j).join('');
      try {
        parts.push(decodeURIComponent(candidate));
        i = j;
        matched = true;
        break;
      } catch {
        // This candidate span doesn't decode — shrink and retry a smaller trailing span.
      }
    }
    if (!matched) {
      // Not even the single token at `i` decodes in isolation (e.g. `%FF`) — leave it literal.
      parts.push(tokens[i]);
      anyUndecodable = true;
      i++;
    }
  }
  return { text: parts.join(''), anyUndecodable };
}

/**
 * Decode every well-formed `%HH` run in `value` exactly once (one pass). A run that fails to
 * decode as a WHOLE is retried via {@link splitDecodeRun} so a valid encoded-PII sub-span inside
 * an otherwise-invalid run is still surfaced (round-19 #8) rather than the entire run being
 * silently left literal. Returns the resulting string, whether anything actually changed (used
 * by the caller to detect a fixed point without a second string comparison), and whether any
 * matched run contained a token that could not be decoded even in isolation — a signal the
 * caller uses to fail closed (a run that matched `%HH` syntax but won't decode is exactly the
 * shape that can hide PII, so it is never treated as proof-of-benign).
 */
function percentDecodeOnePass(value: string): {
  out: string;
  changed: boolean;
  anyRunUndecodable: boolean;
} {
  let changed = false;
  let anyRunUndecodable = false;
  const out = value.replace(PERCENT_ESCAPE_RUN_RE, (run) => {
    try {
      const decoded = decodeURIComponent(run);
      if (decoded !== run) changed = true;
      return decoded;
    } catch {
      // The run matched valid `%HH` syntax but doesn't decode as a whole (e.g. an invalid UTF-8
      // lead byte mixed with otherwise-valid escapes). Best-effort split so any decodable PII
      // sub-span inside the run is still surfaced; fail closed on whatever isn't decodable.
      const { text, anyUndecodable } = splitDecodeRun(run);
      if (anyUndecodable) anyRunUndecodable = true;
      if (text !== run) changed = true;
      return text;
    }
  });
  return { out, changed, anyRunUndecodable };
}

function fullyPercentDecode(value: string): { decoded: string; malformed: boolean } {
  let current = value;
  let sawUndecodableRun = false;
  for (let i = 0; i < MAX_DECODE_ITERATIONS; i++) {
    // Fast exit once a pass no longer contains a `%` at all — nothing left to decode.
    if (current.indexOf('%') < 0) {
      return { decoded: current, malformed: false };
    }
    const { out, changed, anyRunUndecodable } = percentDecodeOnePass(current);
    if (anyRunUndecodable) sawUndecodableRun = true;
    if (!changed) {
      // Fixed point reached — no well-formed escape run decoded to something different
      // (either no `%HH` run is present, or every run present failed to decode). Round-19 #8:
      // a run that MATCHED `%HH` syntax but couldn't be fully decoded (even after the
      // best-effort split) is exactly the shape that can hide PII — fail closed rather than
      // treat the literal fallback as proof of benign. A bare literal `%` that was never
      // matched by the run regex in the first place (CX#12's win, e.g. `50% during
      // spring%20campaign`) never sets this flag, so it still survives untouched.
      return { decoded: out, malformed: sawUndecodableRun };
    }
    current = out;
  }
  // Round-15 #10 / round-19 #9 (over-redaction fix): the loop ran the FULL cap of passes. That
  // is NOT by itself proof of adversarial over-encoding — a value that legitimately carries
  // exactly MAX_DECODE_ITERATIONS layers (e.g. `%2525252F` → 4 valid passes → the benign `/`)
  // is FULLY decoded on the final permitted pass and must NOT be treated as malformed. The
  // sound completeness signal is "no further DECODABLE `%HH` run remains" — not "does any `%`
  // character remain", which over-redacted a benign literal `%` sitting alongside a value that
  // happened to also need the full cap for an unrelated encoded span (`save 50% on
  // %2525252F`). Only fail closed when another well-formed, actually-decodable escape run is
  // STILL present after the cap (would need a 5th pass we refuse to run — can't prove benign),
  // OR a run was seen that matched `%HH` syntax but never decoded (round-19 #8's fail-closed
  // signal).
  if (!sawUndecodableRun && !hasDecodableEscapeRun(current)) {
    return { decoded: current, malformed: false };
  }
  // Still encoded past the cap (or a run never decoded) — adversarially over-encoded, or
  // undecodable. Fail closed.
  return { decoded: current, malformed: true };
}

/**
 * True when `value` contains at least one `%HH` run that would actually decode further (i.e.
 * another pass could make progress). Used ONLY for the post-cap "is anything decodable still
 * left" check (round-19 #9) — a bare literal `%` that is not part of any matched run (or whose
 * matched run fails to decode) must NOT count, otherwise a benign literal `%` sitting anywhere
 * in the string re-triggers the exact over-redaction this check exists to prevent. Bounded by
 * the same {@link MAX_SPLIT_TOKENS}-guarded cost profile as {@link splitDecodeRun}: this only
 * runs once, after the decode cap is already exhausted, and only test-decodes matched runs
 * (never a whole unbounded string with a single `decodeURIComponent`).
 */
function hasDecodableEscapeRun(value: string): boolean {
  PERCENT_ESCAPE_RUN_RE.lastIndex = 0;
  let match: RegExpExecArray | null;
  while ((match = PERCENT_ESCAPE_RUN_RE.exec(value)) !== null) {
    const run = match[0];
    try {
      // A run that decodes to something DIFFERENT from itself still has progress to make.
      if (decodeURIComponent(run) !== run) return true;
    } catch {
      // This run doesn't decode as a whole — round-19 #8's split-decode is what would make
      // progress on it, not a further whole-string pass. Treat as "not a plain decodable run"
      // for this specific check; `sawUndecodableRun` (set during the main pass loop) is what
      // fails closed on the undecodable-run shape, so this function staying narrow here does
      // not reopen that gap.
    }
  }
  return false;
}

/**
 * Round-14 #9: true when a value carries a contact-PII email or LinkedIn URL that becomes
 * visible only after bounded iterative percent-decoding (the singly-decoded value hid it
 * behind an extra encoding layer), OR when valid escapes remain after decoding is abandoned
 * (fail-closed → treat as PII). The singly-decoded checks are done by the callers
 * FIRST; this closes the residual multi-layer channel. Deliberately fail-safe: a false match
 * only over-redacts a benign value, never leaks.
 */
function containsEncodedPiiValue(value: string): boolean {
  if (value.indexOf('%') < 0) return false; // no encoding layer → nothing new to find
  const { decoded, malformed } = fullyPercentDecode(value);
  if (malformed) return true; // cannot prove benign → fail closed
  return containsEmailValue(decoded) || isLinkedInUrlValue(decoded);
}

/**
 * Terminal single-VALUE sanitizer for a telemetry sink (round-10 #10-FE). Given one string that
 * is about to be written to GA4 / BigQuery / any client telemetry, it returns a version with
 * every credential/PII channel closed:
 *   - a LinkedIn profile URL            → `[redacted]` (whole; identifying handle)
 *   - a string CONTAINING an email      → `[redacted]` (whole; a UTM/email value is not a URL)
 *   - otherwise                          → full-URL scrub (token path segment + sensitive query
 *                                          VALUES + fragment) via `redactSecretUrl`
 *
 * The URL scrub is the NARROW fallback (round-10 #24): it only rewrites strings that actually
 * carry a token-path shape or a sensitive query key, so a benign value (`"logout"`, `"admin"`,
 * `"newsletter"`) passes through untouched — legitimate observability is preserved. Non-string
 * input is returned unchanged so callers never have to guard.
 */
export function redactSecretValue(value: unknown): unknown {
  if (typeof value !== 'string' || value.length === 0) return value;
  if (isLinkedInUrlValue(value)) return REDACTED;
  if (containsEmailValue(value)) return REDACTED;
  // Round-14 #9: a terminal telemetry value can also hide its email/LinkedIn PII behind an
  // extra encoding layer that the singly-decoded checks above miss — bounded iterative decode
  // + re-test (fail-closed) closes it here too, keeping this sink symmetric with the query and
  // deep-payload paths. Benign values fall through to the narrow URL scrub unchanged.
  if (containsEncodedPiiValue(value)) return REDACTED;
  return redactSecretUrl(value);
}

/**
 * Array-position markers whose FOLLOWING element is a credential/PII value. React
 * Query keys are positional arrays (no object keys), so a token rides as a bare
 * string AFTER a marker segment — e.g. useOwnerInvite's ['owner','invite', <token>]
 * or a connect key ['connect','respond', <token>]. When an element exactly matches
 * one of these markers (case-insensitively), the next element is redacted.
 */
const SECRET_ARRAY_MARKERS: ReadonlySet<string> = new Set([
  'invite',
  'verify',
  'respond',
  'token',
  'unsubscribe',
  'suppression',
  'suppressions',
]);

function isSecretArrayMarker(value: unknown): boolean {
  return typeof value === 'string' && SECRET_ARRAY_MARKERS.has(value.toLowerCase());
}

const MAX_REDACT_DEPTH = 6;

/**
 * Deep-redact a payload (mutation variables, query-key array, arbitrary context)
 * for safe capture in a telemetry sink:
 *   - object values under a secret-looking KEY  → `[redacted]`
 *   - string values that look like a token-bearing PATH → path-scrubbed
 *   - arrays/objects → recursed (depth-bounded; cycles guarded)
 *   - everything else → passed through
 *
 * Returns a NEW structure (never mutates the input) so the original variables the
 * app still holds are untouched. Depth-bounded and cycle-safe so a pathological
 * payload can never hang or overflow the stack inside an error handler.
 */
export function redactSecretPayload(value: unknown): unknown {
  return redactValue(value, 0, new WeakSet<object>());
}

function redactValue(value: unknown, depth: number, seen: WeakSet<object>): unknown {
  if (value === null || value === undefined) return value;

  if (typeof value === 'string') {
    // A LinkedIn profile URL is directly-identifying contact PII and can ride under a
    // non-secret key (a generic `url`, `returnTo`, a free-text `note`) — value-aware
    // detection redacts it whole regardless of key (round-10 #16). Checked first: a
    // LinkedIn URL is a credential-grade value, so we drop it entirely rather than
    // path/query-scrub it (which would leave the identifying `/in/<handle>` intact).
    if (isLinkedInUrlValue(value)) return REDACTED;
    // Round-13 #10 (§4.3 PII): a nested payload STRING can be a bare email that is
    // not a URL (e.g. a mutation variable `{campaign: 'jane@fund.com'}`), which the
    // `redactSecretUrl` fallback below leaves intact (no token path, no secret query
    // key). Mirror the terminal `redactSecretValue` sanitizer: a string containing an
    // email is directly-identifying PII → redact it whole before the URL scrub. This
    // closes the asymmetry between the deep-payload path and `redactSecretValue`.
    if (containsEmailValue(value)) return REDACTED;
    // Round-14 #9 (§4.3 PII): a nested payload STRING can hide its email/LinkedIn PII
    // behind an EXTRA percent-encoding layer (e.g. a mutation variable
    // `{campaign: 'jane%2540fund.com'}` — double-encoded, no literal `@` after one
    // decode). The singly-decoded `containsEmailValue`/`isLinkedInUrlValue` checks above
    // miss it, and the `redactSecretUrl` fallback below would emit it verbatim. Bounded
    // iterative decode + re-test (fail-closed) mirrors the `redactQueryString` fix so the
    // deep-payload path and the query path close the same multi-layer channel. On a
    // no-match this does NOT rewrite the value — the benign original still flows to the
    // URL scrub below.
    if (containsEncodedPiiValue(value)) return REDACTED;
    // Full-URL scrub (path + sensitive query values + fragment) so a token that
    // rides in a query param (e.g. `?invite=<token>`) inside a mutation variable
    // or query key is caught too, not just a path-segment token.
    return redactSecretUrl(value);
  }

  if (typeof value !== 'object') {
    // number | boolean | bigint | symbol | function — non-PII scalars.
    return value;
  }

  if (depth >= MAX_REDACT_DEPTH) return '[redacted:depth]';

  if (seen.has(value as object)) return '[redacted:circular]';
  seen.add(value as object);

  if (Array.isArray(value)) {
    // Positional redaction: an element directly following a secret marker
    // (e.g. 'invite' → <token>) is the credential and is dropped, not recursed.
    return value.map((item, index) => {
      if (index > 0 && isSecretArrayMarker(value[index - 1])) {
        return REDACTED;
      }
      return redactValue(item, depth + 1, seen);
    });
  }

  const out: Record<string, unknown> = {};
  for (const [key, child] of Object.entries(value as Record<string, unknown>)) {
    if (isSecretKey(key)) {
      out[key] = REDACTED;
    } else {
      out[key] = redactValue(child, depth + 1, seen);
    }
  }
  return out;
}
